import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Wallet, ArrowRight, ArrowLeft, Check, Receipt, PiggyBank, ShieldCheck, Zap, ShoppingCart } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { CURRENCIES, money } from "../lib/format";

const ALLOC = [
  { key: "bills", label: "Bills", icon: Receipt, color: "#EA580C", hint: "Rent, utilities, insurance" },
  { key: "household", label: "Household", icon: ShoppingCart, color: "#65A30D", hint: "Groceries & home items" },
  { key: "savings", label: "Savings", icon: PiggyBank, color: "#0D9488", hint: "Goals & future plans" },
  { key: "emergency", label: "Emergency Fund", icon: ShieldCheck, color: "#0284C7", hint: "Safety net buffer" },
  { key: "unplanned", label: "Unplanned Budget", icon: Zap, color: "#D97706", hint: "Unexpected expenses" },
];

export default function Onboarding() {
  const { user, setUser } = useAuth();
  const [step, setStep] = useState(0);
  const [name, setName] = useState(user?.name || "");
  const [currency, setCurrency] = useState(user?.settings?.currency || "USD");
  const [income, setIncome] = useState("");
  const [cap, setCap] = useState("");
  const [alloc, setAlloc] = useState({ bills: "", household: "", savings: "", emergency: "", unplanned: "" });
  const [saving, setSaving] = useState(false);

  const num = (v) => Number(v || 0);
  const allocTotal = ALLOC.reduce((s, a) => s + num(alloc[a.key]), 0);
  const myWallet = num(income) - allocTotal;

  const steps = ["Profile", "Income", "Allocations", "Spending Cap", "Review"];

  const canNext = () => {
    if (step === 0) return name.trim().length > 0;
    if (step === 1) return num(income) > 0;
    if (step === 3) return num(cap) > 0;
    return true;
  };

  const finish = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/onboarding", {
        name,
        currency,
        monthly_income_target: num(income),
        monthly_spending_cap: num(cap),
        allocations: {
          bills: num(alloc.bills),
          household: num(alloc.household),
          savings: num(alloc.savings),
          emergency: num(alloc.emergency),
          unplanned: num(alloc.unplanned),
        },
      });
      setUser(data);
      toast.success("Your Smart Wallet is ready!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
      <header className="px-6 py-4 flex items-center gap-2.5 border-b border-slate-200 bg-white/80 backdrop-blur">
        <div className="w-9 h-9 rounded-xl bg-orange-600 flex items-center justify-center text-white">
          <Wallet className="w-5 h-5" />
        </div>
        <span className="font-display text-lg font-bold">ePitaka Setup</span>
      </header>

      <div className="flex-1 flex items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-2xl">
          {/* progress */}
          <div className="flex items-center justify-between mb-8">
            {steps.map((s, i) => (
              <div key={s} className="flex-1 flex items-center">
                <div className={`flex items-center gap-2 ${i <= step ? "text-orange-600" : "text-slate-300"}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-colors ${i < step ? "bg-orange-600 border-orange-600 text-white" : i === step ? "border-orange-600 text-orange-600" : "border-slate-200 text-slate-300"}`}>
                    {i < step ? <Check className="w-4 h-4" /> : i + 1}
                  </div>
                  <span className="hidden sm:block text-xs font-medium">{s}</span>
                </div>
                {i < steps.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${i < step ? "bg-orange-600" : "bg-slate-200"}`} />}
              </div>
            ))}
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8">
            <AnimatePresence mode="wait">
              <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
                {step === 0 && (
                  <div className="space-y-5">
                    <div>
                      <h2 className="font-display text-2xl font-bold text-slate-900">Let's set up your profile</h2>
                      <p className="text-slate-500 text-sm mt-1">Choose your display currency — you can switch it anytime.</p>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Your name</Label>
                      <Input data-testid="onboarding-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Alex Rivera" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Display currency</Label>
                      <Select value={currency} onValueChange={setCurrency}>
                        <SelectTrigger data-testid="onboarding-currency-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {CURRENCIES.map((c) => (
                            <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code} — {c.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {step === 1 && (
                  <div className="space-y-5">
                    <div>
                      <h2 className="font-display text-2xl font-bold text-slate-900">Monthly income target</h2>
                      <p className="text-slate-500 text-sm mt-1">Your expected monthly take-home across salary, bonus and side income.</p>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Target income ({currency})</Label>
                      <Input data-testid="onboarding-income-input" type="number" value={income} onChange={(e) => setIncome(e.target.value)} placeholder="5000" className="text-lg font-mono-nums" />
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-5">
                    <div>
                      <h2 className="font-display text-2xl font-bold text-slate-900">Plan your wallet allocations</h2>
                      <p className="text-slate-500 text-sm mt-1">Split your income. Whatever remains becomes your free "My Wallet".</p>
                    </div>
                    <div className="grid gap-3">
                      {ALLOC.map((a) => (
                        <div key={a.key} className="flex items-center gap-3 p-3 rounded-xl border border-slate-200">
                          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${a.color}1a`, color: a.color }}>
                            <a.icon className="w-4.5 h-4.5" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-slate-800">{a.label}</p>
                            <p className="text-xs text-slate-400">{a.hint}</p>
                          </div>
                          <Input data-testid={`onboarding-alloc-${a.key}`} type="number" className="w-32 font-mono-nums"
                            value={alloc[a.key]} onChange={(e) => setAlloc({ ...alloc, [a.key]: e.target.value })} placeholder="0" />
                        </div>
                      ))}
                    </div>
                    <div className={`flex items-center justify-between p-3 rounded-xl text-sm font-medium ${myWallet < 0 ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-700"}`}>
                      <span>My Wallet (free money)</span>
                      <span data-testid="onboarding-my-wallet" className="font-mono-nums font-bold">{money(myWallet, currency)}</span>
                    </div>
                    {myWallet < 0 && <p className="text-xs text-red-500">Allocations exceed your income. Reduce them to keep My Wallet positive.</p>}
                  </div>
                )}

                {step === 3 && (
                  <div className="space-y-5">
                    <div>
                      <h2 className="font-display text-2xl font-bold text-slate-900">Monthly spending cap</h2>
                      <p className="text-slate-500 text-sm mt-1">A soft limit for bills + unplanned spending. We'll warn you — never lock you out.</p>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Soft spending cap ({currency})</Label>
                      <Input data-testid="onboarding-cap-input" type="number" value={cap} onChange={(e) => setCap(e.target.value)} placeholder="3000" className="text-lg font-mono-nums" />
                    </div>
                  </div>
                )}

                {step === 4 && (
                  <div className="space-y-5">
                    <div>
                      <h2 className="font-display text-2xl font-bold text-slate-900">Review & launch</h2>
                      <p className="text-slate-500 text-sm mt-1">Here's your monthly plan. You can edit everything later.</p>
                    </div>
                    <div className="space-y-2.5 text-sm">
                      <Row label="Income target" value={money(income, currency)} />
                      {ALLOC.map((a) => <Row key={a.key} label={a.label} value={money(alloc[a.key], currency)} color={a.color} />)}
                      <Row label="My Wallet (free)" value={money(myWallet, currency)} bold />
                      <Row label="Spending cap" value={money(cap, currency)} />
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            <div className="flex items-center justify-between mt-8 pt-5 border-t border-slate-100">
              <Button variant="ghost" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="onboarding-back-button">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              {step < steps.length - 1 ? (
                <Button onClick={() => setStep((s) => s + 1)} disabled={!canNext()} data-testid="onboarding-next-button" className="bg-orange-600 hover:bg-orange-700 text-white">
                  Continue <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={finish} disabled={saving} data-testid="onboarding-finish-button" className="bg-orange-600 hover:bg-orange-700 text-white">
                  {saving ? "Launching…" : "Launch Dashboard"} <Check className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, color, bold }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
      <span className="text-slate-500 flex items-center gap-2">
        {color && <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />}{label}
      </span>
      <span className={`font-mono-nums ${bold ? "font-bold text-slate-900" : "text-slate-700"}`}>{value}</span>
    </div>
  );
}
